import { Box, type BoxProps } from '@chakra-ui/react';
import { motion, type Transition } from 'motion/react';

const MotionBox = motion.create(Box);

type AnimationVariant =
  | 'fadeIn'
  | 'fadeInUp'
  | 'fadeInDown'
  | 'slideInRight'
  | 'scaleIn'
  | 'popIn'
  | 'slideUp';

type AnimationConfig = {
  initial: Record<string, string | number>;
  animate: Record<string, string | number | number[]>;
  transition: Transition;
};

const STAGGER_STEP = 0.05;

const animations: Record<AnimationVariant, AnimationConfig> = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.4, ease: 'easeOut' },
  },
  fadeInUp: {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
  fadeInDown: {
    initial: { opacity: 0, y: -12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, ease: 'easeOut' },
  },
  slideInRight: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
  scaleIn: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  },
  popIn: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: [0.8, 1.02, 1] },
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
  slideUp: {
    initial: { y: '100%', opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
};

type AnimatedBoxProps = BoxProps & {
  animation?: AnimationVariant;
  stagger?: number;
  delay?: number;
};

export const AnimatedBox = ({
  animation = 'fadeInUp',
  stagger,
  delay,
  ...boxProps
}: AnimatedBoxProps) => {
  const config = animations[animation];
  const animDelay = delay ?? (stagger ? stagger * STAGGER_STEP : 0);

  return (
    <MotionBox
      initial={config.initial}
      animate={config.animate}
      transition={{ ...config.transition, delay: animDelay }}
      {...boxProps}
    />
  );
};
